import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/shadcn/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/shadcn/card';
import { Input } from '../components/shadcn/input';
import { InputOTP } from '../components/shadcn/input-otp';
import { Label } from '../components/shadcn/label';
import { Alert, AlertDescription } from '../components/shadcn/alert';
import { AlertCircle, CheckCircle, QrCode, Key, Shield, Eye, EyeOff } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { setupMFA, verifyMFASetup, generateBackupCodes, isValidTOTPCode } from '../utils/mfa';

const MFASetup = () => {
  const [step, setStep] = useState('intro'); // intro, verify, backup
  const [method] = useState('totp');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const navigate = useNavigate();

  // Setup MFA when method is selected
  const handleSetup = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await setupMFA(method);
      
      if (result.success) {
        setQrCode(result.qrCode);
        setSecret(result.secret);
        setBackupCodes(result.backupCodes);
        setStep('verify');
      } else {
        setError(result.error || 'Failed to setup MFA. Please try again later.');
      }
    } catch (err) {
      setError('MFA service is currently unavailable. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Verify MFA setup
  const handleVerify = async () => {
    // Reset previous states
    setError('');
    setCodeError(false);
    
    if (!isValidTOTPCode(code)) {
      setCodeError(true);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const result = await verifyMFASetup(code, method);
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          setStep('backup');
        }, 1500);
      } else {
        setError(result.error || 'Failed to verify MFA setup. Please try again later.');
      }
    } catch (err) {
      setError('MFA service is currently unavailable. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Generate new backup codes
  const handleGenerateBackupCodes = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await generateBackupCodes();
      
      if (result.success) {
        setBackupCodes(result.backupCodes);
      } else {
        setError(result.error || 'Failed to generate backup codes. Please try again later.');
      }
    } catch (err) {
      setError('MFA service is currently unavailable. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Finish MFA setup
  const handleFinish = () => {
    // In a real app, you would notify the parent component that MFA is complete
    navigate('/dashboard');
  };

  // Combined intro and setup step
  if (step === 'intro') {
    return (
      <div className="w-full max-w-md mx-auto p-4">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl font-bold">Two-Factor Authentication</CardTitle>
            <CardDescription>
              Add an extra layer of security to your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Protect your account with an authenticator app
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <QrCode className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Authenticator App</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Use Google Authenticator, Authy, or similar apps
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <h3 className="font-medium text-gray-900">How it works:</h3>
              <ul className="space-y-1 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">1.</span>
                  <span>Install an authenticator app on your phone</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">2.</span>
                  <span>Scan a QR code or enter a secret key</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">3.</span>
                  <span>Enter the generated code when signing in</span>
                </li>
              </ul>
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleSetup} 
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                'Enable 2FA'
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="w-full max-w-md mx-auto p-4">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <QrCode className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl font-bold">Setup Authenticator</CardTitle>
            <CardDescription>
              Scan the QR code with your authenticator app
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center">
              {qrCode && (
                <div className="p-4 bg-white rounded-lg mb-4">
                  <img 
                    src={qrCode} 
                    alt="MFA QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              )}
              
              <div className="w-full">
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="code" className="text-gray-700">6-digit code</Label>
                  {success && (
                    <span className="text-green-600 text-sm flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Verified
                    </span>
                  )}
                </div>
                <InputOTP
                  id="code"
                  value={code}
                  onChange={(value) => {
                    setCode(value);
                    if (codeError && value.length > 0) {
                      setCodeError(false);
                    }
                  }}
                  maxLength={6}
                  onComplete={handleVerify}
                  className="justify-center"
                  error={codeError}
                  success={success}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Enter the code generated by your authenticator app
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-start">
                <Key className="h-4 w-4 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
                <div className="w-full">
                  <p className="text-xs font-medium text-gray-700 mb-2">Can't scan the QR code?</p>
                  <Alert variant="destructive" className="mb-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Security Warning:</strong> Store this secret securely. Anyone with access to it can generate codes for your account.
                    </AlertDescription>
                  </Alert>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {showSecret ? (
                        <span className="font-mono text-sm bg-white p-2 rounded border">
                          {secret}
                        </span>
                      ) : (
                        <span className="font-mono text-sm bg-white p-2 rounded border">
                          ••••••••••••••••
                        </span>
                      )}
                      <button
                        onClick={() => setShowSecret(!showSecret)}
                        className="ml-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs px-2 py-1 rounded flex items-center"
                        title={showSecret ? "Hide secret" : "Show secret"}
                      >
                        {showSecret ? (
                          <EyeOff className="h-4 w-4 mr-1" />
                        ) : (
                          <Eye className="h-4 w-4 mr-1" />
                        )}
                        {showSecret ? "Hide" : "Show"}
                      </button>
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(secret);
                        // In a real app, you might want to show a toast notification
                      }}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs px-3 py-1 rounded flex items-center"
                      title="Copy secret key to clipboard"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy Key
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {success && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Code verified! Redirecting to next step...
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button 
              onClick={handleVerify}
              disabled={loading || code.length !== 6 || success}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : success ? (
                'Verified'
              ) : (
                'Verify Code'
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setStep('intro')}
              className="w-full"
            >
              Back
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (step === 'backup') {
    return (
      <div className="w-full max-w-md mx-auto p-4">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Key className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl font-bold">Backup Codes</CardTitle>
            <CardDescription>
              Save these codes in a secure location
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Important:</strong> These backup codes are the only way to access your account 
                if you lose your phone. Store them securely.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div 
                  key={index} 
                  className="p-2 bg-gray-100 rounded font-mono text-center text-sm"
                >
                  {code}
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <Button 
                variant="outline" 
                onClick={handleGenerateBackupCodes}
                disabled={loading}
                className="flex-1"
                size="sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Regenerate'
                )}
              </Button>
              <Button 
                onClick={() => {
                  // In a real app, you would print or download these codes
                  alert('In a real application, you would be able to print or download these codes.');
                }}
                className="flex-1"
                size="sm"
              >
                Save
              </Button>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleFinish}
              className="w-full"
            >
              Finish Setup
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return null;
};

export default MFASetup;